/**
 * API Configuration - Istanze Axios per chiamate pubbliche e protette
 */

import axios from 'axios';
import { API_CONFIG, SEARCH_API_CONFIG } from '../config/apiConfig';
import { STORAGE_KEYS } from '../utils/constants';
import { gestisciErroreServer } from './serverRetry';

// Axios instance for public (unprotected) endpoints
export const publicApi = axios.create(API_CONFIG);

// Server non raggiungibile: le GET attendono il ritorno del server e vengono rilanciate, le altre falliscono subito
publicApi.interceptors.response.use(
  (response) => response,
  (error) => gestisciErroreServer(error, publicApi, 'middleware') ?? Promise.reject(error)
);

// Axios instance for protected endpoints (JWT)
export const protectedApi = axios.create(API_CONFIG);

// Axios instance per search-anutel (porta 8081, timeout 60s)
export const searchApi = axios.create(SEARCH_API_CONFIG);

searchApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (token) config.headers['Authorization'] = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

searchApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      window.dispatchEvent(new Event('auth:logout'));
    }
    // Server di ricerca non raggiungibile: le GET attendono il ritorno del server e vengono rilanciate
    return gestisciErroreServer(error, searchApi, 'search') ?? Promise.reject(error);
  }
);

// Variabile per tracciare se il refresh è in corso (evita loop infiniti)
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

/**
 * Reset completo dello stato degli interceptor
 * Da chiamare durante il logout per prevenire memory leak e race condition
 */
export const resetInterceptorState = () => {
  isRefreshing = false;
  failedQueue.forEach((prom) => {
    prom.reject(new Error('Logout effettuato'));
  });
  failedQueue = [];
};

/**
 * Rinnovo unico dell'access token (una sola chiamata /api/Auth/refresh anche con richieste concorrenti).
 * Salva i nuovi token e li restituisce. Se il rinnovo non è possibile rifiuta la promessa
 * (con `erroreOriginale`, se passato, quando manca il refresh token) ed emette `auth:logout`
 * a meno che `emettiLogout` sia false. Le richieste in coda aspettano l'esito del rinnovo in corso.
 */
const rinnovaAccessToken = async ({ erroreOriginale, emettiLogout = true } = {}) => {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;

  const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

  if (!refreshToken) {
    console.error('[api.js] Nessun refresh token - trigger logout');
    isRefreshing = false;
    if (emettiLogout) window.dispatchEvent(new Event('auth:logout'));
    throw erroreOriginale || new Error('Nessun refresh token');
  }

  try {
    const response = await publicApi.post('/api/Auth/refresh', {
      refreshToken,
    });

    const { accessToken, refreshToken: newRefreshToken, expiresAt } = response.data.data;

    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, newRefreshToken);
    localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRY, expiresAt);

    processQueue(null, accessToken);
    isRefreshing = false;
    return accessToken;
  } catch (refreshError) {
    console.error('[api.js] Refresh token fallito:', refreshError.response?.status, refreshError.response?.data);
    processQueue(refreshError, null);
    isRefreshing = false;
    if (emettiLogout) window.dispatchEvent(new Event('auth:logout'));
    throw refreshError;
  }
};

// Access token scaduto o in scadenza entro 60 secondi (TOKEN_EXPIRY non leggibile → false)
const tokenInScadenza = () => {
  const scadenza = Date.parse(localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY));
  return !Number.isNaN(scadenza) && scadenza - Date.now() < 60 * 1000;
};

// Axios instance per endpoint accessibili anche da anonimi: allega il token se presente
// (così il server riconosce l'utente), rinnovandolo prima se scaduto, ma sul 401 NON tenta
// il refresh né porta al login: l'errore torna al chiamante, che decide cosa mostrare.
// Un token scaduto sarebbe trattato dal server come anonimo (endpoint AllowAnonymous).
export const optionalAuthApi = axios.create(API_CONFIG);

optionalAuthApi.interceptors.request.use(
  async (config) => {
    let token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (token && tokenInScadenza() && localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)) {
      try {
        token = await rinnovaAccessToken();
      } catch {
        token = null; // rinnovo fallito: la richiesta prosegue da anonimo, senza redirect
      }
    }
    if (token) config.headers['Authorization'] = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

optionalAuthApi.interceptors.response.use(
  (response) => response,
  (error) => gestisciErroreServer(error, optionalAuthApi, 'middleware') ?? Promise.reject(error)
);

// Attach JWT to protected requests
protectedApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('[api.js] REQUEST interceptor - Errore:', error);
    return Promise.reject(error);
  }
);

// Centralized error handling with automatic token refresh
protectedApi.interceptors.response.use(
  (response) => response,
  async (error) => {

    // Logging dettagliato per errori di validazione (400)
    if (error.response?.status === 400) {
      console.error('[api.js] ===== VALIDATION ERROR 400 =====');
      console.error('[api.js] URL:', error.config?.url);
      console.error('[api.js] Method:', error.config?.method?.toUpperCase());
      console.error('[api.js] Response data:', error.response.data);

      // Se ci sono errori di validazione, loggali dettagliatamente
      if (error.response.data?.errors) {
        console.error('[api.js] VALIDATION ERRORS DETAIL:');
        console.error(JSON.stringify(error.response.data.errors, null, 2));
      }

      console.error('[api.js] ===== END VALIDATION ERROR =====');
    }

    const originalRequest = error.config;

    // Se l'errore è 401 e non abbiamo già tentato il refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const accessToken = await rinnovaAccessToken({
          erroreOriginale: error,
          // Non dispatchare auth:logout se la richiesta fallita è già il logout (evita loop)
          emettiLogout: !originalRequest.url?.includes('/api/Auth/logout'),
        });
        originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
        return protectedApi(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    // Server non raggiungibile (dopo il ramo 401/refresh): le GET attendono il ritorno del server
    // e vengono rilanciate, le altre falliscono subito
    return gestisciErroreServer(error, protectedApi, 'middleware') ?? Promise.reject(error);
  }
);
